import { useEffect, useState } from "react";
import { BREAKPOINTS } from "../styles/breakpoints";

/**
 * useBreakpoint — the ONE way JS learns the viewport band (Fontaine M05).
 *
 * Replaces react-device-detect (user-agent sniffing: wrong on tablets, on resize and in
 * the iframe embed). Backed by matchMedia, so it updates live and matches the CSS mixins.
 *
 * Bands (from tokens.breakpoints — 640 / 900 / 1200 / 1440):
 *   "phone"   < 640     designed target (canvas 375–430)
 *   "tablet"  640–1199  inherits: below 900 the phone layout, 900+ the laptop layout
 *   "laptop"  1200–1439 designed target (canvas 1280–1440)
 *   "wide"    ≥ 1440
 *
 * Use it ONLY where markup must genuinely differ (table → card list, dialog → sheet).
 * Layout differences belong in CSS via the `above()` / `below()` mixins.
 *
 * The four bands answer "which designed target is this?". `isAtLeastMd` answers the different,
 * narrower question "is there room for a laptop-shaped layout?" — 900px, the midpoint the tablet
 * band straddles. It exists because the band model cannot express it: tablet spans 640–1199, so
 * neither `isTablet` nor `<Show below="md">` can tell 700px from 1000px, and the table → card
 * switch (Fontaine M36) turns exactly there.
 *
 * @returns {{ band: "phone"|"tablet"|"laptop"|"wide", isPhone: boolean, isTablet: boolean,
 *            isLaptop: boolean, isWide: boolean, isTabletDown: boolean, isLaptopUp: boolean,
 *            isAtLeastMd: boolean, isTouch: boolean, width: number|null }}
 */
const queries = {
  sm: `(min-width: ${BREAKPOINTS.sm}px)`,
  md: `(min-width: ${BREAKPOINTS.md}px)`,
  lg: `(min-width: ${BREAKPOINTS.lg}px)`,
  xl: `(min-width: ${BREAKPOINTS.xl}px)`,
  touch: "(pointer: coarse)",
};

const canMatch = () => typeof window !== "undefined" && typeof window.matchMedia === "function";

function read() {
  if (!canMatch()) {
    // SSR / tests without matchMedia: assume the laptop target (the wider designed target).
    return { sm: true, md: true, lg: true, xl: false, touch: false, width: null };
  }
  const m = (q) => window.matchMedia(q).matches;
  return { sm: m(queries.sm), md: m(queries.md), lg: m(queries.lg), xl: m(queries.xl), touch: m(queries.touch), width: window.innerWidth };
}

export function bandOf({ sm, lg, xl }) {
  if (xl) return "wide";
  if (lg) return "laptop";
  if (sm) return "tablet";
  return "phone";
}

export default function useBreakpoint() {
  const [state, setState] = useState(read);

  useEffect(() => {
    if (!canMatch()) return undefined;
    const lists = Object.values(queries).map((q) => window.matchMedia(q));
    const update = () => setState(read());
    for (const l of lists) {
      if (l.addEventListener) l.addEventListener("change", update);
      else l.addListener(update);
    }
    update();
    return () => {
      for (const l of lists) {
        if (l.removeEventListener) l.removeEventListener("change", update);
        else l.removeListener(update);
      }
    };
  }, []);

  const band = bandOf(state);
  return {
    band,
    isPhone: band === "phone",
    isTablet: band === "tablet",
    isLaptop: band === "laptop",
    isWide: band === "wide",
    isTabletDown: band === "phone" || band === "tablet",
    isLaptopUp: band === "laptop" || band === "wide",
    // Not a band — the 900px line inside the tablet band. See the note above.
    isAtLeastMd: state.md,
    isTouch: state.touch,
    width: state.width,
  };
}
